import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  HostedAgentLink,
  HostedAgentLinkIdentity,
  HostedAgentLinkMode,
  HostedAgentListItem,
} from "@/api/admin-api"

import { getHostedAgentLinkErrorCode } from "@/api/admin-api"
import { ArrowRight, Key, Trash2, X } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"

import {
  useDeleteHostedAgentLinkMutation,
  useUpdateHostedAgentLinkMutation,
} from "../../hooks/admin-queries"

const LINK_MODES: HostedAgentLinkMode[] = ["ask"]

/**
 * Side panel for one selected AgentLink edge: mode, identity, enabled, delete.
 * Switching identity to `agent` (permission bridge, plan B5) requires the
 * admin to tick an explicit confirmation — the server enforces the same
 * opt-in (`confirm_bridge: true`), the UI just makes the consequence visible.
 */
export function HostedAgentLinkPanel({
  link,
  fromAgent,
  toAgent,
  onClose,
}: {
  link: HostedAgentLink
  fromAgent: HostedAgentListItem | undefined
  toAgent: HostedAgentListItem | undefined
  onClose: () => void
}) {
  const { t } = useTranslation()
  const updateMutation = useUpdateHostedAgentLinkMutation()
  const deleteMutation = useDeleteHostedAgentLinkMutation()

  const [mode, setMode] = useState<HostedAgentLinkMode>(link.mode)
  const [identity, setIdentity] = useState<HostedAgentLinkIdentity>(link.identity)
  const [enabled, setEnabled] = useState(link.enabled)
  const [bridgeConfirmed, setBridgeConfirmed] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Re-seed the form when another edge is selected or the link refetches.
  useEffect(() => {
    setMode(link.mode)
    setIdentity(link.identity)
    setEnabled(link.enabled)
    setBridgeConfirmed(false)
    setConfirmDelete(false)
  }, [link.id, link.mode, link.identity, link.enabled])

  // `mode` has a single member today; compare as strings so wave 3 can widen it.
  const modeChanged = String(mode) !== String(link.mode)
  const isDirty = modeChanged || identity !== link.identity || enabled !== link.enabled
  const switchingToBridge = identity === "agent" && link.identity !== "agent"
  const canSave = isDirty && (!switchingToBridge || bridgeConfirmed)
  const isBusy = updateMutation.isPending || deleteMutation.isPending

  const translateError = (error: unknown) => {
    const code = getHostedAgentLinkErrorCode(error)
    if (code === "bridge_confirmation_required") {
      return t("adminDashboard.hostedAgents.map.errors.bridgeConfirmationRequired", {
        defaultValue: "Confirm the permission bridge before saving an agent-identity link.",
      })
    }
    if (code === "revision_conflict") {
      return t("adminDashboard.hostedAgents.revisionConflict", {
        defaultValue: "The agent changed while you were editing — reload and retry.",
      })
    }
    return error instanceof Error ? error.message : undefined
  }

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        fromAgentId: link.from_agent_id,
        linkId: link.id,
        patch: {
          mode,
          identity,
          enabled,
          ...(identity === "agent" ? { confirm_bridge: true } : {}),
        },
      })
      toast.success(
        t("adminDashboard.hostedAgents.map.linkSaved", { defaultValue: "Link updated" })
      )
    } catch (error) {
      toast.error(
        t("adminDashboard.hostedAgents.map.linkSaveError", {
          defaultValue: "Failed to update the link",
        }),
        { description: translateError(error) }
      )
    }
  }

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    try {
      await deleteMutation.mutateAsync({ fromAgentId: link.from_agent_id, linkId: link.id })
      toast.success(
        t("adminDashboard.hostedAgents.map.linkDeleted", { defaultValue: "Link removed" })
      )
      onClose()
    } catch (error) {
      toast.error(
        t("adminDashboard.hostedAgents.map.linkDeleteError", {
          defaultValue: "Failed to remove the link",
        }),
        { description: translateError(error) }
      )
    }
  }

  return (
    <aside
      aria-label={t("adminDashboard.hostedAgents.map.panelTitle", { defaultValue: "Link" })}
      className="bg-card flex h-full w-full flex-col gap-4 overflow-y-auto rounded-lg border p-4 lg:w-80"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold">
            {t("adminDashboard.hostedAgents.map.panelTitle", { defaultValue: "Link" })}
          </p>
          <p className="text-muted-foreground flex min-w-0 items-center gap-1 text-xs">
            <span className="truncate">{fromAgent?.profile.name ?? link.from_agent_id}</span>
            <ArrowRight aria-hidden="true" className="size-3 shrink-0" />
            <span className="truncate">{toAgent?.profile.name ?? link.to_agent_id}</span>
          </p>
        </div>
        <Button
          size="icon"
          type="button"
          variant="ghost"
          aria-label={t("adminDashboard.hostedAgents.map.closePanel", {
            defaultValue: "Close link panel",
          })}
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>

      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.map.panelIntro", {
          defaultValue:
            "A link lets the first agent ask the second one during a run. Links are brokered by the backend and are off until you enable them; execution arrives in a later wave.",
        })}
      </p>

      <div className="space-y-2">
        <Label htmlFor="hosted-agent-link-mode">
          {t("adminDashboard.hostedAgents.map.modeLabel", { defaultValue: "Mode" })}
        </Label>
        <Select
          disabled={isBusy}
          value={mode}
          onValueChange={(value) => setMode(value as HostedAgentLinkMode)}
        >
          <SelectTrigger id="hosted-agent-link-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LINK_MODES.map((option) => (
              <SelectItem key={option} value={option}>
                {t(`adminDashboard.hostedAgents.map.modes.${option}`, { defaultValue: option })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>
          {t("adminDashboard.hostedAgents.map.identityLabel", { defaultValue: "Identity" })}
        </Label>
        <RadioGroup
          disabled={isBusy}
          value={identity}
          onValueChange={(value) => setIdentity(value as HostedAgentLinkIdentity)}
        >
          <div className="flex items-start gap-2">
            <RadioGroupItem
              className="mt-0.5"
              id="hosted-agent-link-identity-caller"
              value="caller"
            />
            <Label className="flex flex-col gap-0.5" htmlFor="hosted-agent-link-identity-caller">
              <span>
                {t("adminDashboard.hostedAgents.map.identityCaller", { defaultValue: "Caller" })}
              </span>
              <span className="text-muted-foreground text-xs font-normal">
                {t("adminDashboard.hostedAgents.map.identityCallerHelp", {
                  defaultValue:
                    "The target answers filtered by the end user's own permissions. Recommended.",
                })}
              </span>
            </Label>
          </div>
          <div className="flex items-start gap-2">
            <RadioGroupItem
              className="mt-0.5"
              id="hosted-agent-link-identity-agent"
              value="agent"
            />
            <Label className="flex flex-col gap-0.5" htmlFor="hosted-agent-link-identity-agent">
              <span className="flex items-center gap-1">
                <Key aria-hidden="true" className="size-3" />
                {t("adminDashboard.hostedAgents.map.identityAgent", {
                  defaultValue: "Agent (permission bridge)",
                })}
              </span>
              <span className="text-muted-foreground text-xs font-normal">
                {t("adminDashboard.hostedAgents.map.identityAgentHelp", {
                  defaultValue: "The target answers with its own access, regardless of the user.",
                })}
              </span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {identity === "agent" ? (
        <Alert className="border-foreground bg-warning text-warning-foreground [&>svg]:text-warning-foreground">
          <Key className="size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.map.bridgeWarningTitle", {
              defaultValue: "This link bridges permissions",
            })}
          </AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              {t("adminDashboard.hostedAgents.map.bridgeWarningBody", {
                defaultValue:
                  "Anyone who can use the first agent can reach everything the second agent has access to — libraries, tools and data the user could not open directly. Use it only when the target agent is deliberately scoped for that.",
              })}
            </p>
            {switchingToBridge ? (
              <div className="flex items-start gap-2">
                <Checkbox
                  checked={bridgeConfirmed}
                  className="mt-0.5"
                  disabled={isBusy}
                  id="hosted-agent-link-bridge-confirm"
                  onCheckedChange={(checked) => setBridgeConfirmed(checked === true)}
                />
                <Label className="text-xs font-normal" htmlFor="hosted-agent-link-bridge-confirm">
                  {t("adminDashboard.hostedAgents.map.bridgeConfirmLabel", {
                    defaultValue:
                      "I understand that this grants the caller the target agent's access.",
                  })}
                </Label>
              </div>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <Label className="flex flex-col gap-0.5" htmlFor="hosted-agent-link-enabled">
          <span>
            {t("adminDashboard.hostedAgents.map.enabledLabel", { defaultValue: "Enabled" })}
          </span>
          <span className="text-muted-foreground text-xs font-normal">
            {t("adminDashboard.hostedAgents.map.enabledHelp", {
              defaultValue: "Off = the link is registered but no communication is allowed.",
            })}
          </span>
        </Label>
        <Switch
          checked={enabled}
          disabled={isBusy}
          id="hosted-agent-link-enabled"
          onCheckedChange={setEnabled}
        />
      </div>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t pt-3">
        <Button
          disabled={isBusy}
          size="sm"
          type="button"
          variant={confirmDelete ? "destructive" : "ghost"}
          onClick={() => void handleDelete()}
        >
          {deleteMutation.isPending ? (
            <Spinner className="size-4" />
          ) : (
            <Trash2 className="size-4" />
          )}
          {confirmDelete
            ? t("adminDashboard.hostedAgents.map.confirmDelete", { defaultValue: "Really remove?" })
            : t("adminDashboard.hostedAgents.map.deleteLink", { defaultValue: "Remove link" })}
        </Button>
        <Button
          disabled={!canSave || isBusy}
          size="sm"
          type="button"
          onClick={() => void handleSave()}
        >
          {updateMutation.isPending ? <Spinner className="size-4" /> : null}
          {t("adminDashboard.hostedAgents.map.saveLink", { defaultValue: "Save" })}
        </Button>
      </div>
    </aside>
  )
}
